import React, { useRef, useState, useEffect, WheelEvent, MouseEvent } from "react";

const ZoomableImage = ({ src, alt }) => {
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [zoomPercent, setZoomPercent] = useState(100);
    const containerRef = useRef();
    const imageRef = useRef();

    const MIN_SCALE = 0.5;
    const MAX_SCALE = 4;
    const ZOOM_SPEED = 0.1;

    // Handle zoom on scroll
    const handleWheel = (e) => {
        e.preventDefault();

        const delta = e.deltaY * -0.01;
        const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale + delta * ZOOM_SPEED * scale));

        if (containerRef.current && imageRef.current) {
            // Calculate position relative to the image
            const rect = containerRef.current.getBoundingClientRect();
            const x = (e.clientX - rect.left) / scale;
            const y = (e.clientY - rect.top) / scale;

            // Calculate new position to zoom toward mouse position
            const newPositionX = position.x - x * (newScale - scale);
            const newPositionY = position.y - y * (newScale - scale);

            setScale(newScale);
            setPosition({ x: newPositionX, y: newPositionY });
            setZoomPercent(Math.round(newScale * 100));
        }
    };

    // Handle keyboard zoom and controls
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                // Reset zoom
                setScale(1);
                setPosition({ x: 0, y: 0 });
                setZoomPercent(100);
            } else if (e.key === "+" || e.key === "=") {
                // Zoom in
                const newScale = Math.min(MAX_SCALE, scale + ZOOM_SPEED);
                setScale(newScale);
                setZoomPercent(Math.round(newScale * 100));
            } else if (e.key === "-" || e.key === "_") {
                // Zoom out
                const newScale = Math.max(MIN_SCALE, scale - ZOOM_SPEED);
                setScale(newScale);
                setZoomPercent(Math.round(newScale * 100));
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [scale]);

    // Handle mouse drag to move image when zoomed
    const handleMouseDown = (e) => {
        if (scale > 1) {
            setIsDragging(true);
            setDragStart({ x: e.clientX, y: e.clientY });
        }
    };

    const handleMouseMove = (e) => {
        if (isDragging) {
            const dx = (e.clientX - dragStart.x) / scale;
            const dy = (e.clientY - dragStart.y) / scale;
            setPosition({
                x: position.x - dx,
                y: position.y - dy,
            });
            setDragStart({ x: e.clientX, y: e.clientY });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // Handle reset zoom
    const handleResetZoom = () => {
        setScale(1);
        setPosition({ x: 0, y: 0 });
        setZoomPercent(100);
    };

    return (
        <div className="tw-relative tw-justify-center tw-items-center tw-h-full tw-w-full tw-overflow-hidden">
            <div
                ref={containerRef}
                className="tw-h-full tw-w-full tw-flex tw-justify-center tw-items-center"
                onWheel={handleWheel}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
            >
                <div
                    className="tw-h-full tw-w-full tw-transition-transform tw-duration-100 tw-ease-out"
                    style={{
                        transform: `scale(${scale}) translate(${-position.x}px, ${-position.y}px)`,
                        transformOrigin: "center",
                    }}
                >
                    <img ref={imageRef} src={src} alt={alt} className="tw-h-full tw-w-full tw-object-contain" />
                </div>
            </div>

            <div className="tw-absolute tw-top-2 sm:tw-top-4 tw-left-1/2 tw-transform tw--translate-x-1/2 tw-bg-black/60 tw-px-3 tw-py-1 tw-rounded-full tw-text-white tw-text-xs sm:tw-text-sm tw-font-medium tw-z-10">
                {zoomPercent}%
            </div>

            <div className="tw-absolute tw-bottom-2 sm:tw-bottom-4 tw-right-2 sm:tw-right-4 tw-flex tw-space-x-1 sm:tw-space-x-2 tw-z-10">
                <button
                    onClick={() => {
                        const newScale = Math.max(MIN_SCALE, scale - ZOOM_SPEED);
                        setScale(newScale);
                        setZoomPercent(Math.round(newScale * 100));
                    }}
                    className="tw-bg-black/60 tw-text-white tw-w-7 tw-h-7 sm:tw-w-8 sm:tw-h-8 tw-rounded-full tw-flex tw-items-center tw-justify-center hover:tw-bg-black/80 tw-transition-colors tw-text-sm sm:tw-text-base"
                >
                    -
                </button>
                <button
                    onClick={handleResetZoom}
                    className="tw-bg-black/60 tw-text-white tw-px-2 sm:tw-px-3 tw-h-7 sm:tw-h-8 tw-rounded-full tw-flex tw-items-center tw-justify-center hover:tw-bg-black/80 tw-transition-colors tw-text-xs tw-hidden sm:tw-flex"
                >
                    Reset
                </button>
                <button
                    onClick={() => {
                        const newScale = Math.min(MAX_SCALE, scale + ZOOM_SPEED);
                        setScale(newScale);
                        setZoomPercent(Math.round(newScale * 100));
                    }}
                    className="tw-bg-black/60 tw-text-white tw-w-7 tw-h-7 sm:tw-w-8 sm:tw-h-8 tw-rounded-full tw-flex tw-items-center tw-justify-center hover:tw-bg-black/80 tw-transition-colors tw-text-sm sm:tw-text-base"
                >
                    +
                </button>
            </div>
        </div>
    );
};

export default ZoomableImage;
